import { useState, useEffect, useCallback, useContext } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import AppBackService from "../services/appback";
import { RootContext } from "../context/root-provider";
import { permLabel } from "./docSharing";

const styles = {
  banner: {
    border: "1px solid var(--color-border-primary, rgba(128,128,128,0.25))",
    borderRadius: "8px",
    padding: "10px 16px",
    marginBottom: "16px",
    background: "var(--color-bg-secondary, rgba(128,128,128,0.06))",
    textAlign: "left",
  },
  heading: { fontWeight: "bold", fontSize: "0.85rem", opacity: 0.8, paddingTop: "4px" },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
    padding: "6px 0",
  },
  icon: { color: "var(--color-accent-primary)", fontSize: 20 },
  text: { flex: 1, minWidth: "200px" },
  perm: { opacity: 0.7 },
};

/**
 * Banner listing pending doc-share invitations with Accept/Decline (TT-807).
 * Renders nothing when there are no pending invitations.
 */
const PendingShares = ({ onAccepted, showSnackbar }) => {
  const { userInfo, loggingIn } = useContext(RootContext);
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [actingId, setActingId] = useState(null);

  const fetchPending = useCallback(async () => {
    if (!userInfo?.username) return;
    const res = await AppBackService.getPendingShares(userInfo.username);
    setPending(res && !res.error ? res.shares || [] : []);
  }, [userInfo?.username]);

  useEffect(() => {
    if (userInfo?.username && !loggingIn) {
      fetchPending();
    }
  }, [fetchPending, loggingIn, userInfo]);

  const act = useCallback(
    async (share, accept) => {
      setActingId(share.id);
      const call = accept ? AppBackService.acceptShare : AppBackService.declineShare;
      const res = await call(userInfo.username, share.id);
      setActingId(null);
      if (res && !res.error) {
        if (accept) {
          // The toast sits over the top of the list, where the doc just landed:
          // make it the way in rather than something to wait out.
          showSnackbar(`"${share.docTitle}" is now in your documents`, "success", "Open", () =>
            navigate(`/document/${share.docId}`)
          );
        } else {
          showSnackbar("Invitation declined", "success");
        }
        fetchPending();
        if (accept && onAccepted) onAccepted();
      } else if (res?.status === 403) {
        // The share is another account's: a stale banner after an account switch.
        showSnackbar("That invitation isn't for this account", "info");
        fetchPending();
      } else if (res?.status === 404 || res?.status === 409) {
        showSnackbar("That invitation is no longer open", "info");
        fetchPending();
      } else {
        showSnackbar("Couldn't update the invitation. Try again.", "error");
      }
    },
    [fetchPending, navigate, onAccepted, showSnackbar, userInfo?.username]
  );

  if (!pending.length) return null;

  return (
    <Box sx={styles.banner} role="region" aria-label="Shared with you">
      <Typography sx={styles.heading}>
        {pending.length === 1 ? "Shared with you" : `Shared with you (${pending.length})`}
      </Typography>
      {pending.map((share) => (
        <Box key={share.id} sx={styles.row}>
          <PeopleIcon sx={styles.icon} />
          <Typography sx={styles.text}>
            <b>{share.fromUserName || "Someone"}</b> invited you to "{share.docTitle || "a document"}"{" "}
            <Box component="span" sx={styles.perm}>
              · {permLabel(share.permission)}
            </Box>
          </Typography>
          <Button size="small" variant="contained" disabled={actingId === share.id} onClick={() => act(share, true)}>
            Accept
          </Button>
          <Button size="small" disabled={actingId === share.id} onClick={() => act(share, false)}>
            Decline
          </Button>
        </Box>
      ))}
    </Box>
  );
};

PendingShares.propTypes = {
  onAccepted: PropTypes.func,
  showSnackbar: PropTypes.func,
};

export default PendingShares;
